import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import DataGrid, {
  Column,
  Paging,
  Sorting,
  LoadPanel,
  ColumnFixing
} from "devextreme-react/data-grid";
import { TextBox } from "devextreme-react/text-box";
import { NumberBox } from "devextreme-react/number-box";
import AdminDialog from "./AdminDialog";
import {
  getSupplierPricings,
  createSupplierPricing,
  editSupplierPricing,
  deleteSupplierPricing
} from "../../redux/supplierPricing/thunks";
import { getSupplierServices } from "../../redux/supplierServices/thunks";
import {
  selectSupplierPricingsBySupplier,
  selectSupplierPricingLoading
} from "../../redux/supplierPricing/selectors";
import { openModal } from "../../redux/modal/modalSlice";
import editIcon from "../../images/edit.svg";
import deleteIcon from "../../images/delete.svg";
import AddIcon from "@material-ui/icons/Add";

const useStyles = makeStyles({
  wrapper: {
    padding: "0.75rem 1rem 1rem 1rem",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 0,
    margin: "0.5rem 0",
    width: "100%",
    boxSizing: "border-box"
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
    marginBottom: "0.75rem",
    flexWrap: "wrap"
  },
  title: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#334155"
  },
  dialogContentContainer: {
    maxHeight: "65vh",
    overflowY: "auto",
    paddingRight: "0.25rem"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.75rem",
    padding: "0.25rem 0"
  },
  fullWidth: {
    gridColumn: "1 / -1"
  },
  fixedColumn: {
    backgroundColor: "#EFF1F2 !important"
  },
  actionsCell: {
    display: "flex",
    gap: "0.25rem",
    justifyContent: "center"
  },
  deleteConfirmText: {
    fontSize: "0.95rem",
    padding: "1rem 0"
  }
});

const formatRateValue = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed || trimmed === "-") return "-";
    if (trimmed.startsWith("R$")) return trimmed;
    const cleanNum = Number(trimmed.replace(",", "."));
    if (!isNaN(cleanNum)) {
      return `R$ ${cleanNum.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return trimmed;
  }
  if (typeof val === "number") {
    return `R$ ${val.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(val);
};

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return null;
  if (typeof val === "number") return val;
  const n = Number(String(val).replace(",", "."));
  return isNaN(n) ? null : n;
};

const initialFormData = {
  specialtyName: "",
  kmAllowance: null,
  departureNational: null,
  excessKmNational: null,
  departureCapitalSouthSoutheast: null,
  excessKmCapitalSouthSoutheast: null,
  laborCost: null,
  specialServicesPercentage: null,
  specialKm: null,
  dirtRoadKm: null,
  skates: null,
  wheel: null,
  parkingDay: null,
  triggerFee: null,
  towingUp: null,
  offroad: null,
  subwayRetrieval: null,
  stoppedHour: null,
  workedHour: null,
  stoppedHourCaps: null,
  workedHourCaps: null,
  guardianAngel: null,
  softwarePerPlate: null,
  toll: "",
  concierge: null,
  observations: ""
};

function SupplierSpecialtiesDetail(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const service = props.service || props.data?.data || props.data || {};
  const supplierId = props.supplierId || service.supplierId;
  const serviceTypeId = service.supplierServiceTypeId;
  const serviceTypeName = service.serviceTypeName || service.description || "";

  const allPricings = useSelector((state) =>
    selectSupplierPricingsBySupplier(state, supplierId)
  );
  const loading = useSelector(selectSupplierPricingLoading);

  const specialtiesFromProps = props.specialties || service.specialties;
  const pricings = (specialtiesFromProps && specialtiesFromProps.length > 0)
    ? specialtiesFromProps
    : (allPricings || []).filter(
        (p) => Number(p.supplierServiceTypeId) === Number(serviceTypeId)
      );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [operation, setOperation] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const handleOpenCreate = () => {
    setOperation("create");
    setSelectedPricing(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleOpenEdit = (pricing) => {
    setOperation("edit");
    setSelectedPricing(pricing);
    setFormData({
      specialtyName: pricing.specialtyName || "",
      kmAllowance: parseNum(pricing.kmAllowance),
      departureNational: parseNum(pricing.departureNational),
      excessKmNational: parseNum(pricing.excessKmNational),
      departureCapitalSouthSoutheast: parseNum(pricing.departureCapitalSouthSoutheast),
      excessKmCapitalSouthSoutheast: parseNum(pricing.excessKmCapitalSouthSoutheast),
      laborCost: parseNum(pricing.laborCost),
      specialServicesPercentage: parseNum(pricing.specialServicesPercentage),
      specialKm: parseNum(pricing.specialKm),
      dirtRoadKm: parseNum(pricing.dirtRoadKm),
      skates: parseNum(pricing.skates),
      wheel: parseNum(pricing.wheel),
      parkingDay: parseNum(pricing.parkingDay),
      triggerFee: parseNum(pricing.triggerFee),
      towingUp: parseNum(pricing.towingUp),
      offroad: parseNum(pricing.offroad),
      subwayRetrieval: parseNum(pricing.subwayRetrieval),
      stoppedHour: parseNum(pricing.stoppedHour),
      workedHour: parseNum(pricing.workedHour),
      stoppedHourCaps: parseNum(pricing.stoppedHourCaps),
      workedHourCaps: parseNum(pricing.workedHourCaps),
      guardianAngel: parseNum(pricing.guardianAngel),
      softwarePerPlate: parseNum(pricing.softwarePerPlate),
      toll: pricing.toll || "",
      concierge: parseNum(pricing.concierge),
      observations: pricing.observations || ""
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (pricing) => {
    setOperation("delete");
    setSelectedPricing(pricing);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setOperation(null);
    setSelectedPricing(null);
    setFormData(initialFormData);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    if (operation === "delete") {
      dispatch(
        deleteSupplierPricing({ param: selectedPricing.supplierPricingId })
      ).then((res) => {
        if (res.error) {
          dispatch(openModal({ type: "warning", message: res.error.message }));
        } else {
          dispatch(getSupplierPricings({ param: supplierId }));
          dispatch(getSupplierServices({ param: supplierId }));
          handleCloseDialog();
        }
      });
      return;
    }

    if (!formData.specialtyName?.trim()) {
      dispatch(openModal({ type: "warning", message: t("PleaseFillAllFields") }));
      return;
    }

    const formatPayloadVal = (val) => {
      if (val === null || val === undefined || val === "") return null;
      return String(val);
    };

    const payload = {
      supplierId: supplierId,
      supplierServiceTypeId: Number(serviceTypeId),
      specialtyName: formData.specialtyName.trim(),
      kmAllowance: formatPayloadVal(formData.kmAllowance),
      departureNational: formatPayloadVal(formData.departureNational),
      excessKmNational: formatPayloadVal(formData.excessKmNational),
      departureCapitalSouthSoutheast: formatPayloadVal(formData.departureCapitalSouthSoutheast),
      excessKmCapitalSouthSoutheast: formatPayloadVal(formData.excessKmCapitalSouthSoutheast),
      laborCost: formatPayloadVal(formData.laborCost),
      specialServicesPercentage: formatPayloadVal(formData.specialServicesPercentage),
      specialKm: formatPayloadVal(formData.specialKm),
      dirtRoadKm: formatPayloadVal(formData.dirtRoadKm),
      skates: formatPayloadVal(formData.skates),
      wheel: formatPayloadVal(formData.wheel),
      parkingDay: formatPayloadVal(formData.parkingDay),
      triggerFee: formatPayloadVal(formData.triggerFee),
      towingUp: formatPayloadVal(formData.towingUp),
      offroad: formatPayloadVal(formData.offroad),
      subwayRetrieval: formatPayloadVal(formData.subwayRetrieval),
      stoppedHour: formatPayloadVal(formData.stoppedHour),
      workedHour: formatPayloadVal(formData.workedHour),
      stoppedHourCaps: formatPayloadVal(formData.stoppedHourCaps),
      workedHourCaps: formatPayloadVal(formData.workedHourCaps),
      guardianAngel: formatPayloadVal(formData.guardianAngel),
      softwarePerPlate: formatPayloadVal(formData.softwarePerPlate),
      toll: formData.toll?.trim() || null,
      concierge: formatPayloadVal(formData.concierge),
      observations: formData.observations?.trim() || null
    };

    if (operation === "create") {
      dispatch(createSupplierPricing({ body: payload })).then((res) => {
        if (res.error) {
          dispatch(
            openModal({
              type: "warning",
              message: res.error.message || t("PricingAlreadyExists")
            })
          );
        } else {
          dispatch(getSupplierPricings({ param: supplierId }));
          dispatch(getSupplierServices({ param: supplierId }));
          handleCloseDialog();
        }
      });
    } else if (operation === "edit") {
      dispatch(
        editSupplierPricing({
          param: selectedPricing.supplierPricingId,
          body: payload
        })
      ).then((res) => {
        if (res.error) {
          dispatch(
            openModal({
              type: "warning",
              message: res.error.message || t("PricingAlreadyExists")
            })
          );
        } else {
          dispatch(getSupplierPricings({ param: supplierId }));
          dispatch(getSupplierServices({ param: supplierId }));
          handleCloseDialog();
        }
      });
    }
  };

  const renderActionsCell = (cellData) => (
    <div className={classes.actionsCell}>
      <IconButton
        size="small"
        onClick={() => handleOpenEdit(cellData.data)}
        title={t("EditPricing")}
      >
        <img height={16} src={editIcon} alt={t("EditPricing")} />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleOpenDelete(cellData.data)}
        title={t("DeletePricing")}
      >
        <img height={16} src={deleteIcon} alt={t("DeletePricing")} />
      </IconButton>
    </div>
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {t("SpecialtiesAndTariffs")} - {serviceTypeName}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          {t("AddPricing")}
        </Button>
      </div>

      <DataGrid
        dataSource={pricings}
        keyExpr="supplierPricingId"
        showBorders={true}
        showRowLines={true}
        noDataText={t("NoSpecialtiesFoundForService")}
        allowColumnResizing={true}
        columnAutoWidth={true}
        width="100%"
      >
        <ColumnFixing enabled={true} />
        <Paging enabled={false} />
        <Sorting mode="single" />
        <LoadPanel enabled={loading === "loading"} />

        <Column
          dataField="specialtyName"
          caption={t("SpecialtyName")}
          minWidth={150}
          fixed={true}
          fixedPosition="left"
          cssClass={classes.fixedColumn}
        />
        <Column
          dataField="triggerFee"
          caption={t("TriggerFee")}
          width={90}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="kmAllowance"
          caption={t("KmAllowance")}
          width={85}
          alignment="right"
        />
        <Column
          dataField="departureNational"
          caption={t("DepartureNational")}
          width={100}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="excessKmNational"
          caption={t("ExcessKmNational")}
          width={100}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="departureCapitalSouthSoutheast"
          caption={t("DepartureCapitalSouthSoutheast")}
          width={120}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="excessKmCapitalSouthSoutheast"
          caption={t("ExcessKmCapitalSouthSoutheast")}
          width={120}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="laborCost"
          caption={t("LaborCost")}
          width={90}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="specialServicesPercentage"
          caption={t("SpecialServicesPercentage")}
          width={100}
          alignment="right"
        />
        <Column
          dataField="specialKm"
          caption={t("SpecialKm")}
          width={85}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="dirtRoadKm"
          caption={t("DirtRoadKm")}
          width={85}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="skates"
          caption={t("Skates")}
          width={75}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="wheel"
          caption={t("Wheel")}
          width={75}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="parkingDay"
          caption={t("ParkingDay")}
          width={75}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="towingUp"
          caption={t("TowingUp")}
          width={95}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="offroad"
          caption={t("Offroad")}
          width={90}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="subwayRetrieval"
          caption={t("SubwayRetrieval")}
          width={95}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="stoppedHour"
          caption={t("StoppedHour")}
          width={90}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="workedHour"
          caption={t("WorkedHour")}
          width={95}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="stoppedHourCaps"
          caption={t("StoppedHourCaps")}
          width={105}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="workedHourCaps"
          caption={t("WorkedHourCaps")}
          width={110}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="guardianAngel"
          caption={t("GuardianAngel")}
          width={95}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="softwarePerPlate"
          caption={t("SoftwarePerPlate")}
          width={105}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="toll"
          caption={t("Toll")}
          width={80}
        />
        <Column
          dataField="concierge"
          caption={t("Concierge")}
          width={85}
          alignment="right"
          cellRender={(cellData) => formatRateValue(cellData.value)}
        />
        <Column
          dataField="observations"
          caption={t("Observations")}
          minWidth={120}
        />
        <Column
          caption={t("Actions")}
          width={80}
          allowResizing={false}
          fixed={true}
          fixedPosition="right"
          cssClass={classes.fixedColumn}
          cellRender={renderActionsCell}
        />
      </DataGrid>

      <AdminDialog
        title={
          operation === "create"
            ? t("AddPricing")
            : operation === "edit"
            ? t("EditPricing")
            : t("DeletePricing")
        }
        operation={operation}
        dialogSize={{ width: "750px", height: "auto" }}
        open={dialogOpen}
        onClose={handleCloseDialog}
        handleOnClick={handleSubmit}
      >
        {operation === "delete" ? (
          <Typography className={classes.deleteConfirmText}>
            {t("DeletePricingConfirmation")}{" "}
            {selectedPricing ? `"${selectedPricing.specialtyName}"` : ""}
          </Typography>
        ) : (
          <div className={classes.dialogContentContainer}>
            <div className={classes.formGrid}>
              <div className={classes.fullWidth}>
                <TextBox
                  label={t("SpecialtyName")}
                  labelMode="floating"
                  value={formData.specialtyName}
                  onValueChange={(val) =>
                    handleFieldChange("specialtyName", val)
                  }
                />
              </div>
              <NumberBox
                label={t("TriggerFee")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.triggerFee}
                onValueChange={(val) =>
                  handleFieldChange("triggerFee", val)
                }
              />
              <NumberBox
                label={t("KmAllowance")}
                labelMode="floating"
                format="#,##0"
                min={0}
                value={formData.kmAllowance}
                onValueChange={(val) =>
                  handleFieldChange("kmAllowance", val)
                }
              />
              <NumberBox
                label={t("DepartureNational")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.departureNational}
                onValueChange={(val) =>
                  handleFieldChange("departureNational", val)
                }
              />
              <NumberBox
                label={t("ExcessKmNational")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.excessKmNational}
                onValueChange={(val) =>
                  handleFieldChange("excessKmNational", val)
                }
              />
              <NumberBox
                label={t("DepartureCapitalSouthSoutheast")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.departureCapitalSouthSoutheast}
                onValueChange={(val) =>
                  handleFieldChange("departureCapitalSouthSoutheast", val)
                }
              />
              <NumberBox
                label={t("ExcessKmCapitalSouthSoutheast")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.excessKmCapitalSouthSoutheast}
                onValueChange={(val) =>
                  handleFieldChange("excessKmCapitalSouthSoutheast", val)
                }
              />
              <NumberBox
                label={t("LaborCost")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.laborCost}
                onValueChange={(val) =>
                  handleFieldChange("laborCost", val)
                }
              />
              <NumberBox
                label={t("SpecialServicesPercentage")}
                labelMode="floating"
                format="#,##0 '%'"
                min={0}
                max={100}
                value={formData.specialServicesPercentage}
                onValueChange={(val) =>
                  handleFieldChange("specialServicesPercentage", val)
                }
              />
              <NumberBox
                label={t("SpecialKm")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.specialKm}
                onValueChange={(val) =>
                  handleFieldChange("specialKm", val)
                }
              />
              <NumberBox
                label={t("DirtRoadKm")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.dirtRoadKm}
                onValueChange={(val) =>
                  handleFieldChange("dirtRoadKm", val)
                }
              />
              <NumberBox
                label={t("Skates")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.skates}
                onValueChange={(val) =>
                  handleFieldChange("skates", val)
                }
              />
              <NumberBox
                label={t("Wheel")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.wheel}
                onValueChange={(val) =>
                  handleFieldChange("wheel", val)
                }
              />
              <NumberBox
                label={t("ParkingDay")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.parkingDay}
                onValueChange={(val) =>
                  handleFieldChange("parkingDay", val)
                }
              />
              <NumberBox
                label={t("TowingUp")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.towingUp}
                onValueChange={(val) =>
                  handleFieldChange("towingUp", val)
                }
              />
              <NumberBox
                label={t("Offroad")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.offroad}
                onValueChange={(val) =>
                  handleFieldChange("offroad", val)
                }
              />
              <NumberBox
                label={t("SubwayRetrieval")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.subwayRetrieval}
                onValueChange={(val) =>
                  handleFieldChange("subwayRetrieval", val)
                }
              />
              <NumberBox
                label={t("StoppedHour")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.stoppedHour}
                onValueChange={(val) =>
                  handleFieldChange("stoppedHour", val)
                }
              />
              <NumberBox
                label={t("WorkedHour")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.workedHour}
                onValueChange={(val) =>
                  handleFieldChange("workedHour", val)
                }
              />
              <NumberBox
                label={t("StoppedHourCaps")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.stoppedHourCaps}
                onValueChange={(val) =>
                  handleFieldChange("stoppedHourCaps", val)
                }
              />
              <NumberBox
                label={t("WorkedHourCaps")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.workedHourCaps}
                onValueChange={(val) =>
                  handleFieldChange("workedHourCaps", val)
                }
              />
              <NumberBox
                label={t("GuardianAngel")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.guardianAngel}
                onValueChange={(val) =>
                  handleFieldChange("guardianAngel", val)
                }
              />
              <NumberBox
                label={t("SoftwarePerPlate")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.softwarePerPlate}
                onValueChange={(val) =>
                  handleFieldChange("softwarePerPlate", val)
                }
              />
              <TextBox
                label={t("Toll")}
                labelMode="floating"
                value={formData.toll}
                onValueChange={(val) =>
                  handleFieldChange("toll", val)
                }
              />
              <NumberBox
                label={t("Concierge")}
                labelMode="floating"
                format="R$ #,##0.00"
                value={formData.concierge}
                onValueChange={(val) =>
                  handleFieldChange("concierge", val)
                }
              />
              <div className={classes.fullWidth}>
                <TextBox
                  label={t("Observations")}
                  labelMode="floating"
                  value={formData.observations}
                  onValueChange={(val) =>
                    handleFieldChange("observations", val)
                  }
                />
              </div>
            </div>
          </div>
        )}
      </AdminDialog>
    </div>
  );
}

export default SupplierSpecialtiesDetail;
